import { api } from '../lib/api';
import { inviteRoles, isAdmitted, type ParcelMembership, type ParcelSnapshot, type SeatKind } from './model';

/**
 * Run 6: a door may be opened by bearer session (as before) or by a per-seat
 * capability token from the seat URL. With a token, reads and writes go to
 * /api/public and carry the capability header; without one, nothing changes.
 */
const base = (id: string, token?: string | null) =>
  `${token ? '/api/public' : '/api'}/parcels/${encodeURIComponent(id)}`;
const doorApi = (token?: string | null) => !token ? api : {
  get: <T>(path: string) => api.get<T>(path, { headers: { 'x-lg-seat-token': token } }),
  post: <T>(path: string, body?: unknown) => api.post<T>(path, body, { headers: { 'x-lg-seat-token': token } }),
  put: <T>(path: string, body?: unknown) => api.put<T>(path, body, { headers: { 'x-lg-seat-token': token } }),
};
export const parcelApi = {
  list: () => api.get<{ memberships: ParcelMembership[] }>('/api/parcels'),
  provisionJob: (id: string, declared: boolean) => api.post<{ membership: ParcelMembership }>(
    `/api/jobs/${encodeURIComponent(id)}/parcel`, declared ? { statuteBasis: 'FL713_PRIVITY' } : {}),
  provisionProperty: (id: string, county?: string) => api.post<{ membership: ParcelMembership }>(
    `/api/properties/${encodeURIComponent(id)}/parcel`, county ? { county } : {}),
  file: (id: string, token?: string | null) => doorApi(token).get<ParcelSnapshot>(base(id, token)),
  lookup: (county: string, folio: string) => api.get<ParcelSnapshot>(`/api/parcels/lookup?${new URLSearchParams({ county, folio })}`),
  viewAs: (id: string, seat: SeatKind) => api.get<ParcelSnapshot>(`/api/cs/parcels/${encodeURIComponent(id)}/view-as/${seat}`),
  export: (id: string, token?: string | null) => doorApi(token).get<ParcelSnapshot['packet']>(`${base(id, token)}/export`),
  admit: (id: string, seat: SeatKind, token?: string | null) => {
    if (['homeowner', 'flipper', 'title', 'attorney'].includes(seat)) throw new Error('This door cannot self-admit.');
    return doorApi(token).post(`${base(id, token)}/admit`, {
      seat, ...(seat === 'agent' ? {} : { statuteBasis: seat === 'prime' ? 'FL713_PRIVITY' : 'FL713_FURNISHING' }),
    });
  },
};

export type ParcelAction =
  /** Run 10: the agent seat is invited by email — the person; other seats by account ID. */
  | { type: 'invite'; seat: 'agent'; email: string }
  | { type: 'invite'; seat: SeatKind; contractorId: string }
  | { type: 'consent'; seat: 'title' | 'attorney'; contractorId: string }
  | { type: 'title-request' }
  | { type: 'hold-final'; holdFinal: boolean }
  | { type: 'delivery'; kind: 'delivered' | 'will_call' | 'special_fab'; eventDate: string; note: string; attachments: string[] }
  | { type: 'payment'; deliveryId: string; paid: boolean }
  | { type: 'close-listing' }
  | { type: 'unsync' }
  | { type: 'final'; action: 'verify' }
  | { type: 'final'; action: 'override'; reason: string }
  | { type: 'final'; action: 'own_pdf'; pdfReference: string };

export interface ParcelAccess {
  snapshot: ParcelSnapshot; seat: SeatKind; actorId: string; readOnlyAccount: boolean; support: boolean;
  /** Present on a public capability door; routes writes through /api/public. */
  token?: string | null;
}
export function canWrite(access: ParcelAccess): boolean {
  return !access.support && !access.snapshot.readOnly && !access.snapshot.viewAs && !access.readOnlyAccount &&
    !['title', 'attorney'].includes(access.seat) && access.snapshot.seats.some((s) =>
      s.seat === access.seat && s.contractorId === access.actorId && isAdmitted(s));
}
/** UI defense in depth. The authenticated backend rechecks membership and gates on every write. */
export async function mutateParcel(access: ParcelAccess, action: ParcelAction): Promise<unknown> {
  if (!canWrite(access)) throw new Error('This parcel view is read-only.');
  const { snapshot, seat, token } = access;
  const wire = doorApi(token);
  const path = base(snapshot.parcel.id, token);
  switch (action.type) {
    case 'invite':
      if (!inviteRoles[seat]?.includes(action.seat)) break;
      return wire.post(`${path}/invites`, 'email' in action
        ? { seat: action.seat, email: action.email }
        : { seat: action.seat, contractorId: action.contractorId });
    case 'consent':
      if (!['homeowner', 'flipper'].includes(seat) || !snapshot.seats.some((s) =>
        s.seat === action.seat && s.contractorId === action.contractorId && s.invitedAt)) break;
      return wire.post(`${path}/seller-consent`, { seat: action.seat, contractorId: action.contractorId });
    case 'hold-final':
      if (!['homeowner', 'flipper'].includes(seat)) break;
      if (!action.holdFinal && (snapshot.packet.holdFinalReleaseBlocked ||
        snapshot.packet.deliveries.some((d) => d.kind !== 'will_call' && !d.paid))) {
        throw new Error('Unpaid supplier furnishing blocks release of the final-payment hold.');
      }
      return wire.post(`${path}/hold-final`, { holdFinal: action.holdFinal });
    case 'close-listing':
      if (!['homeowner', 'flipper', 'agent'].includes(seat)) break;
      return wire.post(`${path}/listings/close`, {});
    case 'title-request':
      if (seat !== 'agent') break;
      return wire.post(`${path}/title-request`);
    case 'delivery': {
      if (seat !== 'abc') break;
      const { type: _, ...body } = action;
      return wire.post(`${path}/deliveries`, { ...body, source: 'manual' });
    }
    case 'payment':
      if (seat !== 'abc' || !snapshot.packet.deliveries.some((d) => d.id === action.deliveryId &&
        snapshot.seats.some((s) => s.id === d.supplierSeatId && s.contractorId === access.actorId && s.seat === 'abc'))) break;
      return wire.put(`${path}/deliveries/${encodeURIComponent(action.deliveryId)}/payment`, { paid: action.paid });
    case 'unsync':
      if (!['abc', 'roofer'].includes(seat)) break;
      return wire.post(`${path}/unsync`);
    case 'final': {
      if (seat !== 'roofer') break;
      if (action.action === 'verify' && (!snapshot.packet.cleanDraftEligible || snapshot.packet.deliveries.some((d) => d.kind !== 'will_call' && !d.paid))) {
        throw new Error('Unpaid supplier furnishing blocks verification.');
      }
      if (action.action === 'override' && !action.reason.trim()) throw new Error('An override reason is required.');
      if (action.action === 'own_pdf' && !action.pdfReference.trim()) throw new Error('A PDF reference is required.');
      const { type: _, ...body } = action;
      return wire.post(`${path}/roofer-final`, body);
    }
  }
  throw new Error('This seat cannot perform that action.');
}
